import { TextStyle } from 'editor/src/store/design/types';
import { FontDefinition } from 'editor/src/store/fonts/types';

import { getBrowserFriendlyColor, parseColorToRGBA } from 'editor/src/util/color/colorUtils';

import type React from 'react';

// 1 pt = 4/3 CSS px. Previews are clamped so a 120 pt heading still fits a card and a
// 6 pt caption stays readable; the meta line says the real size.
const PT_TO_PX = 4 / 3;
export const PREVIEW_MIN_PX = 12;
export const PREVIEW_MAX_PX = 40;

/** Sizes read back from fabric carry float noise (28.000000000000004). */
export const formatNumber = (value: number | undefined) =>
  value === undefined ? '' : String(Math.round(value * 100) / 100);

export const getPreviewFontSize = (fontSize: number, max = PREVIEW_MAX_PX) =>
  Math.round(Math.min(max, Math.max(PREVIEW_MIN_PX, fontSize * PT_TO_PX)));

/**
 * The CSS for a name rendered in the style's own look. The font face is registered under its
 * font file name once loaded (loadFont); until then, and for protected fonts that have no file,
 * the browser falls back to the sans-serif.
 */
export const getTextStylePreviewCss = (style: TextStyle, maxPx = PREVIEW_MAX_PX): React.CSSProperties => ({
  fontFamily: `"${style.fontFamily}", sans-serif`,
  fontSize: `${getPreviewFontSize(style.fontSize, maxPx)}px`,
  color: style.color ? getBrowserFriendlyColor(style.color) : undefined,
  textDecoration:
    [style.underline ? 'underline' : '', style.linethrough ? 'line-through' : ''].filter(Boolean).join(' ') ||
    undefined,
  // Thousandths of an em, as on the canvas (fabric charSpacing).
  letterSpacing: style.letterSpacing ? `${style.letterSpacing / 1000}em` : undefined,
});

/** A near-white style would vanish on the light card, so its preview gets a dark backdrop. */
export function isLightColor(color: string | undefined): boolean {
  if (!color) {
    return false;
  }
  const { r, g, b } = parseColorToRGBA(color);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 225;
}

/** "Playfair Display Bold" from the font's metadata, the font file name when the font is unknown. */
export function getFontDisplayName(fontFamily: string, font: FontDefinition | undefined): string {
  if (!font) {
    return fontFamily;
  }
  const { name, weight, style } = font.metadata;
  return [name, weight && weight !== 'Regular' ? weight : '', style && style !== 'Normal' ? style : '']
    .filter(Boolean)
    .join(' ');
}
